import React, { useMemo, useEffect, useState } from "react";
import { Stack } from "@fluentui/react";
import SummaryTable from "./SummaryTable";
import { IBrand } from "../../../shared/interfaces/IReceipAnalyzerEntities";
import { useAppContext } from "./AppContext";
import { useSearchParams } from "react-router";
import { useNavigate } from 'react-router';
import ProgressBar from './ProgressBar';
import {
    ResponsiveContainer, ComposedChart, Line, Area,
    XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const TOOLTIP_STYLE = {
    contentStyle: {
        background: '#111',
        border: '1px solid rgba(255,204,0,0.3)',
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: '0.72rem',
    },
    itemStyle: { color: '#fff' },
    labelStyle: { color: '#ffcc00', fontWeight: 700 },
} as const;

const AXIS_TICK = { fontSize: 10, fontFamily: 'inherit', fill: '#000' };

interface StockPoint { date: string; close: number }

// Well-known corporation → ticker mappings
const TICKER_MAP: Record<string, string> = {
    'the coca-cola company': 'KO', 'coca-cola': 'KO',
    'pepsico': 'PEP', 'pepsi': 'PEP',
    'nestlé': 'NESN.SW', 'nestle': 'NESN.SW',
    'procter & gamble': 'PG', 'procter and gamble': 'PG',
    'unilever': 'UL',
    'mondelez': 'MDLZ', 'mondelēz': 'MDLZ',
    'danone': 'BN.PA',
    'mars': 'MARS',
    'kraft heinz': 'KHC',
    'general mills': 'GIS',
    'kellogg': 'K', "kellogg's": 'K',
    'colgate-palmolive': 'CL', 'colgate': 'CL',
    'johnson & johnson': 'JNJ',
    'henkel': 'HEN.DE',
    'reckitt': 'RKT.L', 'reckitt benckiser': 'RKT.L',
    'l\'oréal': 'OR.PA', 'loreal': 'OR.PA', 'l\'oreal': 'OR.PA',
    'beiersdorf': 'BEI.DE',
    'ferrero': 'FERRERO',
    'apple': 'AAPL',
    'microsoft': 'MSFT',
    'amazon': 'AMZN',
    'walmart': 'WMT',
    'costco': 'COST',
    'target': 'TGT',
    'nike': 'NKE',
    'adidas': 'ADS.DE',
    'lvmh': 'MC.PA',
    'ab inbev': 'ABI.BR', 'anheuser-busch': 'ABI.BR',
    'diageo': 'DGE.L',
    'heineken': 'HEIA.AS',
    'starbucks': 'SBUX',
    'mcdonald\'s': 'MCD', 'mcdonalds': 'MCD',
};

function guessTickerSymbol(corporationName: string): string | null {
    const lower = corporationName.toLowerCase().trim();
    // Direct match
    if (TICKER_MAP[lower]) return TICKER_MAP[lower];
    // Partial match
    for (const [key, ticker] of Object.entries(TICKER_MAP)) {
        if (lower.includes(key) || key.includes(lower)) return ticker;
    }
    return null;
}

export default function Corporation() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { appData } = useAppContext();

    const corporationId = searchParams.get("corporationId");
    const brands = appData.data.brands?.filter(b => b.owning_corporation === corporationId) ?? [];
    const totalAmount = brands.reduce((sum, b) => sum + (b.total || 0), 0);
    const totalPurchases = brands.reduce((sum, b) => sum + Number(b.purchases || 0), 0);

    const currency = appData.user.currency || '';
    const receipts = appData.data.reportReceipts ?? [];
    const items = appData.data.reportProductItems ?? [];
    const brandNames = useMemo(() => new Set(brands.map(b => b.name)), [brands]);

    // ── Stock data ─────────────────────────────────────────────────────────
    const [stockData, setStockData] = useState<StockPoint[]>([]);
    const [stockCurrency, setStockCurrency] = useState('');
    const [stockSymbol, setStockSymbol] = useState('');
    const [stockLoading, setStockLoading] = useState(false);

    useEffect(() => {
        if (!corporationId) return;
        const ticker = guessTickerSymbol(corporationId);
        const params = ticker
            ? `symbol=${encodeURIComponent(ticker)}`
            : `name=${encodeURIComponent(corporationId)}`;
        setStockLoading(true);
        fetch(`/api/stock?${params}`)
            .then(r => r.ok ? r.json() : null)
            .then(json => {
                if (json?.data) {
                    setStockData(json.data);
                    setStockCurrency(json.currency || '');
                    setStockSymbol(json.symbol || ticker || '');
                }
            })
            .catch(() => {})
            .finally(() => setStockLoading(false));
    }, [corporationId]);

    // ── Monthly purchase evolution (spending at this corporation over time) ──
    const monthlyPurchases = useMemo(() => {
        const corpItems = items.filter(i => brandNames.has(i.brand));
        const map = new Map<string, { total: number; count: number }>();
        corpItems.forEach(i => {
            const date = i.receipt_transaction_date;
            if (!date) return;
            const ym = date.substring(0, 7);
            const cur = map.get(ym) || { total: 0, count: 0 };
            cur.total += i.total_price || 0;
            cur.count += 1;
            map.set(ym, cur);
        });
        return map;
    }, [items, brandNames]);

    // ── Price evolution (per-product unit price, carry-forward, averaged across products) ──
    const priceByMonth = useMemo(() => {
        const corpItems = items.filter(i => brandNames.has(i.brand) && i.total_price != null && i.quantity);

        // Step 1: Per product per month, compute unit price
        const productMonthPrice = new Map<string, Map<string, { spent: number; qty: number }>>();
        const allMonths = new Set<string>();
        corpItems.forEach(i => {
            const date = i.receipt_transaction_date;
            if (!date) return;
            const productKey = i.product_identifier ?? i.description ?? '';
            const ym = date.substring(0, 7);
            allMonths.add(ym);
            if (!productMonthPrice.has(productKey)) productMonthPrice.set(productKey, new Map());
            const monthMap = productMonthPrice.get(productKey)!;
            const cur = monthMap.get(ym) || { spent: 0, qty: 0 };
            cur.spent += i.total_price || 0;
            cur.qty += i.quantity || 0;
            monthMap.set(ym, cur);
        });

        // Step 2: Carry forward last known price per product
        const sortedMonths = Array.from(allMonths).sort();
        const productTimeline = new Map<string, Map<string, number>>();
        productMonthPrice.forEach((monthMap, productKey) => {
            const timeline = new Map<string, number>();
            let lastPrice: number | null = null;
            for (const ym of sortedMonths) {
                const data = monthMap.get(ym);
                if (data) lastPrice = data.spent / data.qty;
                if (lastPrice != null) timeline.set(ym, lastPrice);
            }
            productTimeline.set(productKey, timeline);
        });

        // Step 3: For each month, average per-product prices
        const result = new Map<string, { sum: number; count: number }>();
        for (const ym of sortedMonths) {
            let sum = 0;
            let count = 0;
            productTimeline.forEach(timeline => {
                const price = timeline.get(ym);
                if (price != null) { sum += price; count += 1; }
            });
            if (count > 0) result.set(ym, { sum, count });
        }
        return result;
    }, [items, brandNames]);

    // ── Stock data aggregated to monthly ───────────────────────────────────
    const stockByMonth = useMemo(() => {
        const map = new Map<string, { sum: number; count: number }>();
        stockData.forEach(d => {
            const ym = d.date.substring(0, 7);
            const cur = map.get(ym) || { sum: 0, count: 0 };
            cur.sum += d.close;
            cur.count += 1;
            map.set(ym, cur);
        });
        return map;
    }, [stockData]);

    // ── Combined chart data (all 3 series normalised to index = 100) ──────
    const combinedChart = useMemo(() => {
        const allMonths = new Set<string>();
        monthlyPurchases.forEach((_, ym) => allMonths.add(ym));
        priceByMonth.forEach((_, ym) => allMonths.add(ym));
        stockByMonth.forEach((_, ym) => allMonths.add(ym));
        const sorted = Array.from(allMonths).sort();
        if (sorted.length === 0) return [];

        // Raw values per month
        const rows = sorted.map(ym => {
            const spend = monthlyPurchases.get(ym);
            const price = priceByMonth.get(ym);
            const stock = stockByMonth.get(ym);
            return {
                ym,
                spending: spend ? Math.round(spend.total * 100) / 100 : undefined as number | undefined,
                avgPrice: price ? Math.round((price.sum / price.count) * 100) / 100 : undefined as number | undefined,
                stockPrice: stock ? Math.round((stock.sum / stock.count) * 100) / 100 : undefined as number | undefined,
            };
        });

        // Find first non-undefined value for each series to use as index base
        const baseSpend = rows.find(r => r.spending !== undefined)?.spending;
        const basePrice = rows.find(r => r.avgPrice !== undefined)?.avgPrice;
        const baseStock = rows.find(r => r.stockPrice !== undefined)?.stockPrice;

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        return rows.map(r => {
            const [year, month] = r.ym.split('-');
            return {
                month: `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`,
                spending: r.spending !== undefined && baseSpend ? Math.round((r.spending / baseSpend) * 100) : undefined,
                avgPrice: r.avgPrice !== undefined && basePrice ? Math.round((r.avgPrice / basePrice) * 100) : undefined,
                stockPrice: r.stockPrice !== undefined && baseStock ? Math.round((r.stockPrice / baseStock) * 100) : undefined,
                // Keep raw values for tooltip
                _rawSpend: r.spending,
                _rawPrice: r.avgPrice,
                _rawStock: r.stockPrice,
            };
        });
    }, [monthlyPurchases, priceByMonth, stockByMonth]);

    const hasSpending = combinedChart.some(d => d.spending !== undefined);
    const hasPrice = combinedChart.some(d => d.avgPrice !== undefined);
    const hasStock = combinedChart.some(d => d.stockPrice !== undefined);

    if (!appData.dataFetched) {
        return <ProgressBar label="Loading" progress={20} />;
    }

    if (!corporationId || brands.length === 0) {
        navigate('/');
        return null;
    }

    return (
        <Stack horizontalAlign="center" style={{ width: '100%', margin: '0 auto' }}>
            <div className="entity-summary">
                <br/>
                <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '0 auto', padding: '18px 0' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, margin: 6, letterSpacing: 1 }}>
                        {corporationId}
                    </div>
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                        <strong>Total:</strong> {totalAmount.toFixed(2)} {currency}
                        {' · '}
                        <strong>Purchases:</strong> {totalPurchases}
                    </div>
                </div>

                {/* ── Combined Trends Chart ─────────────────────────────── */}
                {combinedChart.length > 1 && (hasSpending || hasPrice || hasStock) && (
                    <div style={{ width: '90%', margin: '12px auto 0' }}>
                        <div className="chart-card">
                            <span className="chart-title">
                                Trends
                                <span style={{ fontSize: '0.65rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.5 }}>
                                    {' '}— indexed to 100
                                </span>
                            </span>
                            <ResponsiveContainer width="100%" height={240}>
                                <ComposedChart data={combinedChart}>
                                    <defs>
                                        <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#000" stopOpacity={0.12} />
                                            <stop offset="100%" stopColor="#000" stopOpacity={0.01} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                    <XAxis dataKey="month" tick={AXIS_TICK} />
                                    <YAxis tick={AXIS_TICK} />
                                    <Tooltip
                                        {...TOOLTIP_STYLE}
                                        formatter={((value: any, name: any, props: any) => {
                                            const d = props.payload;
                                            if (name === 'spending') return [`${currency} ${d._rawSpend?.toLocaleString() ?? '–'}  (${value})`, 'Spending'];
                                            if (name === 'avgPrice') return [`${currency} ${d._rawPrice?.toLocaleString() ?? '–'}  (${value})`, 'Avg Price'];
                                            if (name === 'stockPrice') return [`${stockCurrency} ${d._rawStock?.toLocaleString() ?? '–'}  (${value})`, `${stockSymbol} Stock`];
                                            return [value, name];
                                        }) as any}
                                    />
                                    <Legend wrapperStyle={{ fontSize: '0.7rem', fontFamily: 'inherit' }} />
                                    {hasSpending && <Area type="monotone" dataKey="spending" stroke="#000" strokeWidth={2} fill="url(#spendGrad)" name="spending" connectNulls />}
                                    {hasPrice && <Line type="monotone" dataKey="avgPrice" stroke="#806000" strokeWidth={2} dot={{ r: 2, fill: '#806000' }} name="avgPrice" connectNulls />}
                                    {hasStock && <Line type="monotone" dataKey="stockPrice" stroke="#1a5f3c" strokeWidth={2} dot={false} name="stockPrice" connectNulls />}
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
                {stockLoading && (
                    <div style={{ width: '90%', margin: '12px auto 0', textAlign: 'center', fontSize: '0.75rem', opacity: 0.5 }}>
                        Loading stock data…
                    </div>
                )}

                <div>
                    <br/>
                    <strong style={{ fontSize: 14 }}>Brands:</strong>
                    <br/>
                    <br/>
                    <Stack horizontalAlign="center" style={{ margin: 'auto', width: '90%' }}>
                    <SummaryTable
                        data={brands.sort((a, b) => (b.total ?? 0) - (a.total ?? 0)).map((item, idx) => ({ id: idx, ...item }))}
                        columns={[
                        {
                            key: "name",
                            label: "Brand",
                            width: "50%",
                            total: false,
                            render: (_: any, item: IBrand) => (
                                <span className='table-cell-content' onClick={() => navigate(`/Brand?brandId=${encodeURIComponent(item.name)}`)} style={{ cursor: 'pointer' }}>{item.name}</span>
                            ),
                        },
                        { key: "purchases", label: "Purchases", width: "33%", total: false },
                        { key: "total", label: "Total", width: "22%", total: true },
                        ]}
                        filterKeys={[]}
                        header={true}
                        total={true}
                    />
                    </Stack>
                    <br/>
                </div>
            </div>
        </Stack>
    );
}
